/**
 * Roční (sezónní) souhrny včelstva – čisté výpočty nad zápisy, bez databáze.
 */
import { colonyEvents } from './group';
import type { Colony, ColonyEvent, ColonyStatus, ISODate, Queen } from './model';
import { feedingSugarKg, infestationPercent, isMiteFall, mitesPerDay } from './rules';
import { inSeason, seasonOf, type Season } from './season';

export interface YearStats {
  season: Season;
  events: number;
  // med
  honeyKg: number;
  harvests: { date: ISODate; kg: number; honeyType: string; waterPercent?: number }[];
  // krmení
  /** Krmení celkem v kg cukru (přepočteno podle druhu krmiva). */
  feedSugarKg: number;
  feedings: { date: ISODate; amount: number; unit: 'kg' | 'l'; sugarKg: number; feedType: string }[];
  // léčení
  treatments: { date: ISODate; product: string; batch?: string }[];
  // varroáza
  varroaChecks: { date: ISODate; miteCount: number; perDay: number | null; percent: number | null }[];
  maxMitesPerDay: number | null;
  /** Celkový spad roztočů za rok (součet podložek a spadů po ošetření); null = žádná taková kontrola. */
  totalMites: number | null;
  maxInfestation: number | null;
  sentToLab: boolean;
  // prohlídky
  inspections: number;
  maxSeams: number | null;
  maxBroodFrames: number | null;
  avgTemperament: number | null;
  swarmCells: boolean;
  diseaseSuspicion: boolean;
  // matka a rojení
  swarms: number;
  queenChanges: number;
  splits: number;
  queens: Pick<Queen, 'year' | 'line' | 'marked' | 'markColor'>[];
  // medníky
  supersAdded: number;
  supersRemoved: number;
  firstSuperDate?: ISODate;
  lastSuperRemovedDate?: ISODate;
  // stav
  statusChanges: { date: ISODate; status: ColonyStatus; cause?: string }[];
  endStatus: ColonyStatus;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Podíl včelstva na medobraní (u společného medobraní poměrně). */
export function honeyShare(e: Extract<ColonyEvent, { type: 'harvest' }>, colonyId: string): number {
  if (e.colonyIds.length === 1) return e.honeyKg;
  return e.perColonyKg?.[colonyId] ?? e.honeyKg / e.colonyIds.length;
}

export function yearStats(
  colony: Pick<Colony, 'id' | 'status'>,
  allEvents: ColonyEvent[],
  queens: Queen[],
  s: Season,
): YearStats {
  const evs = colonyEvents(allEvents, colony.id)
    .filter((e) => inSeason(e.date, s))
    .sort((a, b) => a.date.localeCompare(b.date));
  const st: YearStats = {
    season: s,
    events: evs.length,
    honeyKg: 0,
    harvests: [],
    feedSugarKg: 0,
    feedings: [],
    treatments: [],
    varroaChecks: [],
    maxMitesPerDay: null,
    totalMites: null,
    maxInfestation: null,
    sentToLab: false,
    inspections: 0,
    maxSeams: null,
    maxBroodFrames: null,
    avgTemperament: null,
    swarmCells: false,
    diseaseSuspicion: false,
    swarms: 0,
    queenChanges: 0,
    splits: 0,
    queens: [],
    supersAdded: 0,
    supersRemoved: 0,
    statusChanges: [],
    endStatus: 'active',
  };
  const temperaments: number[] = [];
  for (const e of evs) {
    switch (e.type) {
      case 'harvest': {
        const kg = honeyShare(e, colony.id);
        st.honeyKg += kg;
        st.harvests.push({ date: e.date, kg: round1(kg), honeyType: e.honeyType, waterPercent: e.waterPercent });
        break;
      }
      case 'feeding':
      {
        const sugar = feedingSugarKg(e);
        st.feedSugarKg += sugar;
        st.feedings.push({ date: e.date, amount: e.amountPerColony, unit: e.unit, sugarKg: round1(sugar), feedType: e.feedType });
      }
        break;
      case 'treatment':
        st.treatments.push({ date: e.date, product: e.productName, batch: e.batchNumber });
        break;
      case 'varroa_check': {
        const perDay = mitesPerDay(e);
        const pct = infestationPercent(e);
        if (e.miteCount != null) st.varroaChecks.push({ date: e.date, miteCount: e.miteCount, perDay, percent: pct });
        if (perDay != null) st.maxMitesPerDay = Math.max(st.maxMitesPerDay ?? 0, perDay);
        if (isMiteFall(e) && e.miteCount != null) st.totalMites = (st.totalMites ?? 0) + e.miteCount;
        if (pct != null) st.maxInfestation = Math.max(st.maxInfestation ?? 0, pct);
        if (e.sentToLab) st.sentToLab = true;
        break;
      }
      case 'inspection':
        st.inspections++;
        if (e.occupiedSeams != null) st.maxSeams = Math.max(st.maxSeams ?? 0, e.occupiedSeams);
        if (e.broodFrames != null) st.maxBroodFrames = Math.max(st.maxBroodFrames ?? 0, e.broodFrames);
        if (e.temperament) temperaments.push(e.temperament);
        if (e.queenCells === 'swarm') st.swarmCells = true;
        if (e.diseaseSuspicion) st.diseaseSuspicion = true;
        break;
      case 'swarm':
        st.swarms++;
        break;
      case 'queen_change':
        st.queenChanges++;
        break;
      case 'split':
        st.splits++;
        break;
      case 'super_change':
        if (e.action === 'add') {
          st.supersAdded += e.count;
          if (!st.firstSuperDate) st.firstSuperDate = e.date;
        } else {
          st.supersRemoved += e.count;
          st.lastSuperRemovedDate = e.date;
        }
        break;
      case 'status_change':
        st.statusChanges.push({ date: e.date, status: e.newStatus, cause: e.cause });
        break;
    }
  }
  st.honeyKg = round1(st.honeyKg);
  st.feedSugarKg = round1(st.feedSugarKg);
  if (temperaments.length) st.avgTemperament = round1(temperaments.reduce((a, b) => a + b, 0) / temperaments.length);
  if (st.maxMitesPerDay != null) st.maxMitesPerDay = round1(st.maxMitesPerDay);
  if (st.maxInfestation != null) st.maxInfestation = round1(st.maxInfestation);

  // stav na konci sezóny: poslední změna stavu v sezóně, jinak „žije“
  st.endStatus = st.statusChanges.length ? st.statusChanges[st.statusChanges.length - 1].status : 'active';

  // matky, které byly ve včelstvu během sezóny
  st.queens = queens
    .filter(
      (q) =>
        !q.deletedAt &&
        q.colonyId === colony.id &&
        (!q.introducedAt || q.introducedAt <= s.to) &&
        (!q.removedAt || q.removedAt >= s.from),
    )
    .sort((a, b) => (a.introducedAt ?? '').localeCompare(b.introducedAt ?? ''))
    .map((q) => ({ year: q.year, line: q.line, marked: q.marked, markColor: q.markColor }));
  return st;
}

/** Sezóny (od nejnovější), ve kterých má včelstvo nějaký zápis, plus aktuální sezóna. */
export function seasonsWithData(events: ColonyEvent[], startMonth: number, today: ISODate): number[] {
  const years = new Set<number>([seasonOf(today, startMonth).year]);
  for (const e of events) if (!e.deletedAt) years.add(seasonOf(e.date, startMonth).year);
  return [...years].sort((a, b) => b - a);
}

/** Šipka změny oproti předchozímu roku. */
export function trend(cur: number | null, prev: number | null): '↑' | '↓' | '=' | '' {
  if (cur == null || prev == null) return '';
  if (Math.abs(cur - prev) < 0.05) return '=';
  return cur > prev ? '↑' : '↓';
}
